import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Users, UsersRound } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESET_COLORS = [
    '#845642', '#3E4A3D', '#C48A54', '#4A6B53', '#D98342',
    '#6B4433', '#2A2725', '#A64444', '#5C7A6F', '#8B7355'
];

const CommitteesPage = () => {
    const [committees, setCommittees] = useState([]);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCommittee, setEditingCommittee] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        color: '#845642'
    });

    useEffect(() => {
        fetchCommittees();
        fetchMembers();
    }, []);

    const fetchCommittees = async () => {
        try {
            const response = await axios.get(`${API}/committees`);
            setCommittees(response.data);
        } catch (error) {
            toast.error('Failed to load committees');
        } finally {
            setLoading(false);
        }
    };

    const fetchMembers = async () => {
        try {
            const response = await axios.get(`${API}/members`);
            setMembers(response.data);
        } catch (error) {
            console.error('Failed to load members');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingCommittee) {
                await axios.put(`${API}/committees/${editingCommittee.id}`, formData);
                toast.success('Committee updated successfully');
            } else {
                await axios.post(`${API}/committees`, formData);
                toast.success('Committee created successfully');
            }
            setIsModalOpen(false);
            resetForm();
            fetchCommittees();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save committee');
        }
    };

    const handleEdit = (committee) => {
        setEditingCommittee(committee);
        setFormData({
            name: committee.name,
            description: committee.description || '',
            color: committee.color || '#845642'
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (committeeId) => {
        if (!window.confirm('Are you sure you want to delete this committee?')) return;
        try {
            await axios.delete(`${API}/committees/${committeeId}`);
            toast.success('Committee deleted successfully');
            fetchCommittees();
        } catch (error) {
            toast.error('Failed to delete committee');
        }
    };

    const resetForm = () => {
        setEditingCommittee(null);
        setFormData({
            name: '',
            description: '',
            color: '#845642'
        });
    };

    const getMemberCount = (committeeId) => {
        return members.filter(m => m.committee_ids?.includes(committeeId)).length;
    };

    const getCommitteeMembers = (committeeId) => {
        return members.filter(m => m.committee_ids?.includes(committeeId));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-[#A0A0A0]">Loading committees...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="committees-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold text-white font-['Playfair_Display'] tracking-tight">
                        Committees
                    </h1>
                    <p className="text-[#A0A0A0] mt-1">
                        Manage volunteer leadership committees
                    </p>
                </div>
                <Dialog open={isModalOpen} onOpenChange={(open) => {
                    setIsModalOpen(open);
                    if (!open) resetForm();
                }}>
                    <DialogTrigger asChild>
                        <Button 
                            className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                            data-testid="add-committee-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Committee
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle className="text-2xl font-bold text-white font-['Playfair_Display']">
                                {editingCommittee ? 'Edit Committee' : 'Create Committee'}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Committee Name *</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                    placeholder="e.g., Mentorship"
                                    data-testid="committee-name-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="description">Description</Label>
                                <Textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="What does this committee do?"
                                    rows={3}
                                    data-testid="committee-description-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Color</Label>
                                <div className="flex flex-wrap gap-2">
                                    {PRESET_COLORS.map((color) => (
                                        <button
                                            key={color}
                                            type="button"
                                            onClick={() => setFormData({ ...formData, color })}
                                            className={`w-8 h-8 rounded-full transition-transform ${
                                                formData.color === color ? 'ring-2 ring-offset-2 ring-[#C9A227] scale-110' : ''
                                            }`}
                                            style={{ backgroundColor: color }}
                                            data-testid={`color-${color}`}
                                        />
                                    ))}
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setIsModalOpen(false);
                                        resetForm();
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    type="submit"
                                    className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                                    data-testid="save-committee-btn"
                                >
                                    {editingCommittee ? 'Update' : 'Create'}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Committees Grid */}
            {committees.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
                    {committees.map((committee) => (
                        <Card 
                            key={committee.id} 
                            className="border-[#5A6350] shadow-sm card-hover overflow-hidden"
                            data-testid={`committee-card-${committee.id}`}
                        >
                            <div 
                                className="h-2"
                                style={{ backgroundColor: committee.color }}
                            />
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div 
                                            className="w-12 h-12 rounded-xl flex items-center justify-center text-white flex-shrink-0"
                                            style={{ backgroundColor: committee.color }}
                                        >
                                            <UsersRound className="w-6 h-6" />
                                        </div>
                                        <div className="min-w-0">
                                            <h3 className="font-semibold text-[#3D4435] text-lg truncate">{committee.name}</h3>
                                            <p className="text-sm text-[#A0A0A0]">
                                                {getMemberCount(committee.id)} members
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex gap-1 flex-shrink-0">
                                        <button
                                            onClick={() => handleEdit(committee)}
                                            className="p-2 hover:bg-[#3D4435] rounded-lg transition-colors"
                                            data-testid={`edit-committee-${committee.id}`}
                                        >
                                            <Edit2 className="w-4 h-4 text-[#A0A0A0]" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(committee.id)}
                                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                                            data-testid={`delete-committee-${committee.id}`}
                                        >
                                            <Trash2 className="w-4 h-4 text-[#A64444]" />
                                        </button>
                                    </div>
                                </div>

                                {committee.description && (
                                    <p className="text-sm text-[#A0A0A0] mb-4 line-clamp-2">
                                        {committee.description}
                                    </p>
                                )}

                                {/* Member avatars */}
                                <div className="flex items-center">
                                    <div className="flex -space-x-2">
                                        {getCommitteeMembers(committee.id).slice(0, 5).map((member) => (
                                            <div
                                                key={member.id}
                                                className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-white text-xs font-medium"
                                                style={{ backgroundColor: committee.color }}
                                                title={member.name}
                                            >
                                                {member.name?.charAt(0).toUpperCase()}
                                            </div>
                                        ))}
                                    </div>
                                    {getMemberCount(committee.id) > 5 && (
                                        <span className="ml-2 text-sm text-[#A0A0A0]">
                                            +{getMemberCount(committee.id) - 5} more
                                        </span>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : (
                <Card className="border-[#5A6350]">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                        <UsersRound className="w-16 h-16 text-[#E6E1D8] mb-4" />
                        <h3 className="text-lg font-semibold text-white mb-2">No committees yet</h3>
                        <p className="text-[#A0A0A0] text-center mb-4">
                            Create your first committee to organize members
                        </p>
                        <Button 
                            onClick={() => setIsModalOpen(true)}
                            className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Create Committee
                        </Button>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default CommitteesPage;
